import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { Plus, RefreshCw, Trash2, Wifi } from "lucide-react";
import useConfirmDialog from "../../../../hooks/useConfirmDialog";
import {
  applyExternalAttendancePunch,
  getDefaultDeviceIntegration,
  loadDeviceIntegrations,
  saveDeviceIntegrations,
  syncApiDeviceIntegration,
} from "../../../../lib/attendanceDeviceIntegration";

const BiometricDevicesIntegrationPanel = ({ darkMode, canEdit = true }) => {
  const { confirm, dialogNode } = useConfirmDialog();
  const [devices, setDevices] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [testEmpId, setTestEmpId] = useState("");
  const [mapExternal, setMapExternal] = useState("");
  const [mapInternal, setMapInternal] = useState("");

  const reload = () => {
    const { devices: list } = loadDeviceIntegrations();
    setDevices(list);
    if (list.length && !list.find((d) => d.id === selectedId)) {
      setSelectedId(list[0]?.id ?? null);
    }
    if (!list.length) setSelectedId(null);
  };

  useEffect(() => {
    reload();
  }, []);

  const selected =
    devices.find((d) => d.id === selectedId) || devices[0] || null;

  const updateSelected = (patch) => {
    if (!selected) return;
    const next = devices.map((d) =>
      d.id === selected.id ? { ...d, ...patch } : d
    );
    setDevices(next);
    saveDeviceIntegrations({ devices: next });
  };

  const addDevice = () => {
    const d = getDefaultDeviceIntegration();
    d.name = `Device ${devices.length + 1}`;
    const next = [...devices, d];
    setDevices(next);
    setSelectedId(d.id);
    saveDeviceIntegrations({ devices: next });
  };

  const removeDevice = (id) => {
    const next = devices.filter((d) => d.id !== id);
    setDevices(next);
    saveDeviceIntegrations({ devices: next });
    if (selectedId === id) setSelectedId(next[0]?.id ?? null);
  };

  const saveAll = () => {
    saveDeviceIntegrations({ devices });
    toast.success("Device settings saved");
  };

  const runSync = async () => {
    if (!selected || selected.type !== "api") {
      toast.error("Select an enabled API integration to sync");
      return;
    }
    setSyncing(true);
    try {
      const result = await syncApiDeviceIntegration(selected);
      const updated = {
        ...selected,
        lastSyncAt: Date.now(),
        lastSyncStatus: result.ok ? "ok" : "error",
        lastSyncMessage: result.message,
      };
      const next = devices.map((d) => (d.id === selected.id ? updated : d));
      setDevices(next);
      saveDeviceIntegrations({ devices: next });
      if (result.ok) toast.success(result.message);
      else toast.error(result.message);
    } finally {
      setSyncing(false);
    }
  };

  const testPunch = async (eventType) => {
    const empId = testEmpId.trim();
    if (!empId) {
      toast.error("Enter HRMS employee ID to test");
      return;
    }
    try {
      const res = await applyExternalAttendancePunch({
        employeeId: empId,
        eventType,
        callApi: true,
        device: selected,
      });
      if (res.skipped) toast(res.reason, { icon: "ℹ️" });
      else {
        (res.warnings || []).forEach((w) => toast(w, { duration: 5000, icon: "⚠️" }));
        toast.success(`${eventType === "check_in" ? "Check-in" : "Check-out"} recorded`);
      }
    } catch (err) {
      toast.error(err?.message || "Punch failed");
    }
  };

  const addIdMapping = () => {
    if (!selected || !mapExternal.trim() || !mapInternal.trim()) return;
    const employeeIdMap = {
      ...(selected.employeeIdMap || {}),
      [mapExternal.trim()]: mapInternal.trim(),
    };
    updateSelected({ employeeIdMap });
    setMapExternal("");
    setMapInternal("");
    toast.success("Mapping added");
  };

  const border = darkMode ? "border-[hsl(var(--ds-border))]" : "border-[hsl(var(--ds-border))]";
  const inputCls = `w-full rounded-md border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] px-2 py-1.5 text-sm text-[hsl(var(--ds-fg))]`;
  const labelCls = "ds-caption mb-1 block text-[hsl(var(--ds-muted-fg))]";

  return (
    <div className="space-y-4">
      {dialogNode}
      <div>
        <h4 className="ds-h4 text-[hsl(var(--ds-fg))]">
          Biometric devices & attendance API
        </h4>
        <p className="ds-caption mt-1 text-[hsl(var(--ds-muted-fg))]">
          Connect fingerprint/face devices or third-party attendance software. Punches are
          stored per employee the same way as the navbar check-in button and drive PR/LOP
          calculation.
        </p>
      </div>

      {canEdit !== false ? (
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={addDevice}
          className="inline-flex items-center gap-1.5 rounded-md bg-[hsl(var(--ds-primary))] px-3 py-1.5 text-sm font-semibold text-[hsl(var(--ds-primary-fg))] hover:bg-[hsl(var(--ds-primary-hover))]"
        >
          <Plus className="h-4 w-4" />
          Add device / API
        </button>
        <button type="button" onClick={saveAll} className={`rounded-md border px-3 py-1.5 text-sm ${border}`}>
          Save all
        </button>
      </div>
      ) : (
        <p className="text-xs text-[hsl(var(--ds-muted-fg))]">View only — you cannot add or save devices.</p>
      )}

      <div className="flex flex-col gap-4 lg:flex-row lg:min-h-[420px]">
        <ul className={`lg:w-56 shrink-0 rounded-lg border ${border} divide-y dark:divide-[hsl(var(--ds-border))]`}>
          {devices.length === 0 ? (
            <li className="p-4 text-xs text-[hsl(var(--ds-muted-fg))]">No devices configured yet.</li>
          ) : (
            devices.map((d) => (
              <li
                key={d.id}
                className={`flex items-center gap-1 ${
                  selected?.id === d.id
                    ? "border-l-[3px] border-l-[hsl(var(--ds-primary))] bg-[hsl(var(--ds-primary)/0.08)] font-semibold text-[hsl(var(--ds-primary))]"
                    : "border-l-[3px] border-l-transparent hover:bg-[hsl(var(--ds-muted))]"
                }`}
              >
                <button
                  type="button"
                  onClick={() => setSelectedId(d.id)}
                  className="min-w-0 flex-1 px-3 py-2.5 text-left text-sm"
                >
                  <span className="truncate block">
                    {d.name || "Unnamed"}
                    <span className="block text-[10px] opacity-70">
                      {d.type === "api" ? "API" : "Biometric"}
                      {d.enabled ? "" : " · off"}
                    </span>
                  </span>
                </button>
                <button
                  type="button"
                  className="shrink-0 rounded-md p-2 text-[hsl(var(--ds-danger))] hover:bg-[hsl(var(--ds-danger)/0.1)]"
                  onClick={async () => {
                    const ok = await confirm({
                      title: "Remove integration",
                      message: "Remove this integration?",
                      confirmText: "Remove",
                      cancelText: "Cancel",
                      danger: true,
                    });
                    if (ok) removeDevice(d.id);
                  }}
                  aria-label="Remove"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </li>
            ))
          )}
        </ul>

        {selected ? (
          <div className={`flex-1 rounded-lg border p-4 space-y-4 ${border}`}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>Name</label>
                <input
                  className={inputCls}
                  value={selected.name}
                  onChange={(e) => updateSelected({ name: e.target.value })}
                />
              </div>
              <div>
                <label className={labelCls}>Type</label>
                <select
                  className={inputCls}
                  value={selected.type}
                  onChange={(e) => updateSelected({ type: e.target.value })}
                >
                  <option value="biometric">Biometric device</option>
                  <option value="api">Attendance software API</option>
                </select>
              </div>
              <div className="sm:col-span-2 flex items-center gap-2">
                <input
                  type="checkbox"
                  id="dev-enabled"
                  checked={!!selected.enabled}
                  onChange={(e) => updateSelected({ enabled: e.target.checked })}
                />
                <label htmlFor="dev-enabled" className="text-sm">
                  Enabled
                </label>
              </div>
            </div>

            {selected.type === "biometric" ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className={labelCls}>Device serial / ID</label>
                  <input
                    className={inputCls}
                    value={selected.deviceSerial || ""}
                    onChange={(e) => updateSelected({ deviceSerial: e.target.value })}
                    placeholder="e.g. ZKTECO-001"
                  />
                </div>
                <div>
                  <label className={labelCls}>Location</label>
                  <input
                    className={inputCls}
                    value={selected.location || ""}
                    onChange={(e) => updateSelected({ location: e.target.value })}
                    placeholder="e.g. Main gate"
                  />
                </div>
                <p className="sm:col-span-2 text-xs text-[hsl(var(--ds-muted-fg))]">
                  Map device user IDs to HRMS employee IDs below. When your middleware posts
                  punches, call the same punch helpers with the resolved employee id.
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="sm:col-span-2">
                  <label className={labelCls}>Base URL</label>
                  <input
                    className={inputCls}
                    value={selected.baseUrl || ""}
                    onChange={(e) => updateSelected({ baseUrl: e.target.value })}
                    placeholder="https://attendance.vendor.com"
                  />
                </div>
                <div>
                  <label className={labelCls}>API key / token</label>
                  <input
                    type="password"
                    className={inputCls}
                    value={selected.apiKey || ""}
                    onChange={(e) => updateSelected({ apiKey: e.target.value })}
                  />
                </div>
                <div>
                  <label className={labelCls}>Punches endpoint path</label>
                  <input
                    className={inputCls}
                    value={selected.punchesPath || "/api/punches"}
                    onChange={(e) => updateSelected({ punchesPath: e.target.value })}
                  />
                </div>
                <div>
                  <label className={labelCls}>Poll interval (minutes)</label>
                  <input
                    type="number"
                    min={1}
                    className={inputCls}
                    value={selected.pollIntervalMinutes ?? 15}
                    onChange={(e) =>
                      updateSelected({
                        pollIntervalMinutes: Number(e.target.value) || 15,
                      })
                    }
                  />
                </div>
                <div>
                  <label className={labelCls}>Employee id field in JSON</label>
                  <input
                    className={inputCls}
                    value={selected.employeeIdField || "employeeId"}
                    onChange={(e) => updateSelected({ employeeIdField: e.target.value })}
                  />
                </div>
                <div className="sm:col-span-2 flex items-center gap-2">
                  <button
                    type="button"
                    disabled={syncing}
                    onClick={runSync}
                    className="inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-sm inline-flex items-center justify-center rounded-md bg-[hsl(var(--ds-primary))] px-3 py-1.5 text-sm font-semibold text-[hsl(var(--ds-primary-fg))] hover:bg-[hsl(var(--ds-primary-hover))] disabled:opacity-45 text-white"
                  >
                    <RefreshCw className={`h-4 w-4 ${syncing ? "animate-spin" : ""}`} />
                    Sync now
                  </button>
                  {selected.lastSyncMessage && (
                    <span className="text-xs text-[hsl(var(--ds-muted-fg))]">
                      Last: {selected.lastSyncStatus} — {selected.lastSyncMessage}
                    </span>
                  )}
                </div>
              </div>
            )}

            <div className={`rounded border p-3 ${border}`}>
              <p className="text-sm font-medium mb-2 flex items-center gap-1">
                <Wifi className="h-4 w-4" />
                Employee ID mapping
              </p>
              <div className="flex flex-wrap gap-2 mb-2">
                <input
                  className={`${inputCls} max-w-[10rem]`}
                  placeholder="Device / external ID"
                  value={mapExternal}
                  onChange={(e) => setMapExternal(e.target.value)}
                />
                <input
                  className={`${inputCls} max-w-[10rem]`}
                  placeholder="HRMS employee ID"
                  value={mapInternal}
                  onChange={(e) => setMapInternal(e.target.value)}
                />
                <button type="button" onClick={addIdMapping} className="rounded border px-2 py-1 text-sm">
                  Add map
                </button>
              </div>
              {Object.keys(selected.employeeIdMap || {}).length > 0 ? (
                <ul className="text-xs space-y-1 max-h-24 overflow-y-auto">
                  {Object.entries(selected.employeeIdMap).map(([ext, int]) => (
                    <li key={ext} className="font-mono">
                      {ext} → {int}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-xs text-[hsl(var(--ds-muted-fg))]">No mappings yet.</p>
              )}
            </div>

            <div className={`rounded border p-3 ${border} bg-[hsl(var(--ds-muted))]/50 dark:bg-[hsl(var(--ds-surface))]/30`}>
              <p className="text-sm font-medium mb-2">Test punch (same as navbar)</p>
              <div className="flex flex-wrap gap-2 items-center">
                <input
                  className={`${inputCls} max-w-[12rem]`}
                  placeholder="HRMS employee ID"
                  value={testEmpId}
                  onChange={(e) => setTestEmpId(e.target.value)}
                />
                <button
                  type="button"
                  onClick={() => testPunch("check_in")}
                  className="rounded-md border px-3 py-1.5 text-sm"
                >
                  Test check-in
                </button>
                <button
                  type="button"
                  onClick={() => testPunch("check_out")}
                  className="rounded-md border px-3 py-1.5 text-sm"
                >
                  Test check-out
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div
            className={`flex-1 flex items-center justify-center rounded-lg border border-dashed p-8 text-sm text-[hsl(var(--ds-muted-fg))] ${border}`}
          >
            Add a device or API integration to configure.
          </div>
        )}
      </div>
    </div>
  );
};

export default BiometricDevicesIntegrationPanel;
